import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Phone, Mail, Anchor, Users, Star, Crown, Compass, Shield, Clock, ArrowRight } from 'lucide-react';

// Service data
const serviceData = {
  'private-escapes': {
    name: 'Private Escapes',
    subtitle: 'Exceptional Journeys',
    description: 'Create your bespoke escape with our specialized consultants. Each journey is unique and reflects your most personal desires.',
    heroImage: 'https://images.unsplash.com/photo-1578662996442-48f60103fc96',
    features: [
      {
        icon: <Crown className="w-6 h-6" />,
        title: 'Ultra-Personalized Service',
        description: 'A dedicated consultant to create your perfect journey according to your tastes and desires.'
      },
      {
        icon: <Compass className="w-6 h-6" />,
        title: 'Exclusive Destinations',
        description: 'Access to private anchorages and secret destinations reserved for our clientele.'
      },
      {
        icon: <Shield className="w-6 h-6" />,
        title: 'Absolute Confidentiality',
        description: 'Discretion and confidentiality guaranteed for your peace of mind.'
      },
      {
        icon: <Users className="w-6 h-6" />,
        title: 'Elite Crew',
        description: 'Hand-picked crews trained to the most demanding standards.'
      }
    ],
    packages: [
      {
        name: 'Weekend Escape',
        duration: '2-3 days',
        description: 'The perfect getaway for a luxurious break',
        price: 'From €15,000',
        includes: [
          'Luxury yacht with full crew',
          'Personalized itinerary',
          'Exceptional gastronomy',
          '24/7 concierge service'
        ]
      },
      {
        name: 'Week Escape',
        duration: '7 days',
        description: 'A week of total escape in absolute luxury',
        price: 'From €45,000',
        includes: [
          'Super yacht with expert crew',
          'Multiple destinations',
          'Private starred chef',
          'Exclusive nautical activities',
          'Helipad for transfers'
        ]
      },
      {
        name: 'Exclusive Escape',
        duration: '14+ days',
        description: 'The ultimate experience for connoisseurs',
        price: 'On request',
        includes: [
          'Exceptional mega yacht',
          'Complete bespoke itinerary',
          'Palace-level crew',
          'Unique and private experiences',
          'Unlimited concierge service'
        ]
      }
    ]
  },
  'exclusive-collection': {
    name: 'Exclusive Collection',
    subtitle: 'Exceptional Fleet',
    description: 'Discover our collection of exceptional yachts, selected for their design, performance and unparalleled refinement.',
    heroImage: 'https://images.unsplash.com/photo-1569263979104-865ab7cd8d13',
    features: [
      {
        icon: <Star className="w-6 h-6" />,
        title: 'Award-Winning Yachts',
        description: 'Each yacht in our collection has been rewarded for its excellence.'
      },
      {
        icon: <Crown className="w-6 h-6" />,
        title: 'Exceptional Design',
        description: 'Naval architectures signed by the world\'s greatest designers.'
      },
      {
        icon: <Shield className="w-6 h-6" />,
        title: 'Perfect Maintenance',
        description: 'Impeccable condition guaranteed through our rigorous preventive maintenance.'
      },
      {
        icon: <Users className="w-6 h-6" />,
        title: 'Certified Crews',
        description: 'Captains and crews certified to the highest international standards.'
      }
    ],
    packages: [
      {
        name: 'Prestige Collection',
        duration: 'Motor Yachts 40-60m',
        description: 'Motor yachts combining performance and elegance',
        price: '€8,000 - €25,000/day',
        includes: [
          'Refined contemporary design',
          'Speeds up to 30 knots',
          'Complete nautical equipment',
          'Generous living spaces'
        ]
      },
      {
        name: 'Royal Collection',
        duration: 'Super Yachts 60-100m',
        description: 'Super yachts for the most refined experiences',
        price: '€25,000 - €65,000/day',
        includes: [
          'Exceptional architectures',
          'Spas and wellness centers',
          'Certified helipads',
          'Palatial lounges and suites'
        ]
      },
      {
        name: 'Imperial Collection',
        duration: 'Mega Yachts 100m+',
        description: 'The pinnacle of maritime luxury',
        price: 'On request',
        includes: [
          'Unique floating palaces',
          '20+ person crews',
          'Bespoke equipment',
          'Experiences impossible elsewhere'
        ]
      }
    ]
  },
  'bespoke-experiences': {
    name: 'Bespoke Experiences',
    subtitle: 'Unique Creations',
    description: 'Our experts create unique experiences that exceed your wildest dreams. Every detail is designed for you.',
    heroImage: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b',
    features: [
      {
        icon: <Compass className="w-6 h-6" />,
        title: 'Personalized Design',
        description: 'Each experience is designed specifically according to your tastes and desires.'
      },
      {
        icon: <Star className="w-6 h-6" />,
        title: 'Partners of Excellence',
        description: 'Exclusive network of hand-picked partners worldwide.'
      },
      {
        icon: <Crown className="w-6 h-6" />,
        title: 'Privileged Access',
        description: 'Opening doors normally closed thanks to our exclusive relationships.'
      },
      {
        icon: <Clock className="w-6 h-6" />,
        title: '24/7 Availability',
        description: 'Our teams are available at any time to fulfill your wishes.'
      }
    ],
    packages: [
      {
        name: 'Cultural Experience',
        duration: 'Bespoke',
        description: 'Exclusive cultural discoveries and privileged encounters',
        price: 'From €5,000',
        includes: [
          'Private expert guides',
          'Access to sites closed to public',
          'Meetings with local artists',
          'Immersive cultural experiences'
        ]
      },
      {
        name: 'Gastronomic Experience',
        duration: 'Bespoke',
        description: 'Culinary journeys with the greatest chefs',
        price: 'From €8,000',
        includes: [
          'Private starred chefs',
          'Exclusive market visits',
          'Tastings in the best cellars',
          'Personalized cooking classes'
        ]
      },
      {
        name: 'Adventure Experience',
        duration: 'Bespoke',
        description: 'Extraordinary adventures in exceptional places',
        price: 'From €12,000',
        includes: [
          'Exclusive nautical activities',
          'Private underwater explorations',
          'Helicopter overflights',
          'Bespoke adventures'
        ]
      }
    ]
  },
  'maritime-concierge': {
    name: 'Maritime Concierge',
    subtitle: 'Excellence Service',
    description: 'Our maritime concierge service anticipates and fulfills all your wishes, 24/7, wherever you are at sea.',
    heroImage: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b',
    features: [
      {
        icon: <Clock className="w-6 h-6" />,
        title: 'Permanent Availability',
        description: '24/7 service worldwide to meet all your needs.'
      },
      {
        icon: <Shield className="w-6 h-6" />,
        title: 'Immediate Resolution',
        description: 'Expert teams capable of resolving any situation quickly.'
      },
      {
        icon: <Users className="w-6 h-6" />,
        title: 'Global Network',
        description: 'Trusted partners in all ports and destinations.'
      },
      {
        icon: <Crown className="w-6 h-6" />,
        title: 'Impossible Requests',
        description: 'Specialized in fulfilling exceptional demands.'
      }
    ],
    packages: [
      {
        name: 'Essential Concierge',
        duration: 'Basic service',
        description: 'The essentials for a worry-free journey',
        price: 'Included in our services',
        includes: [
          'Restaurant and activity reservations',
          'Ground transport organization',
          'Administrative assistance',
          '24/7 technical support'
        ]
      },
      {
        name: 'Premium Concierge',
        duration: 'Extended service',
        description: 'Anticipation and fulfillment of all your wishes',
        price: '€2,000/day',
        includes: [
          'Dedicated personal shopper',
          'Private event organization',
          'Exclusive reservations',
          'Specialized medical services'
        ]
      },
      {
        name: 'Unlimited Concierge',
        duration: 'Absolute service',
        description: 'No limit to your requests',
        price: 'On request',
        includes: [
          'Permanent dedicated team',
          'Fulfillment of impossible requests',
          'Ultra-exclusive network access',
          'Creation of unique experiences'
        ]
      }
    ]
  }
};

// Header Component for service pages
const Header = ({ isTransparent = false }) => {
  const [isScrolled, setIsScrolled] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const shouldBeTransparent = isTransparent && !isScrolled;

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${shouldBeTransparent ? 'bg-transparent' : 'bg-white/95 backdrop-blur-lg shadow-sm'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center">
            <a href="/" className={`text-2xl font-light tracking-wide transition-colors ${shouldBeTransparent ? 'text-white' : 'text-charcoal-900'}`}>
              PRIVIA<span className="font-thin">CHARTER</span>
            </a>
          </div>
          
          <nav className="hidden lg:flex space-x-12">
            <a href="/escapes" className={`text-sm font-light tracking-wide transition-all hover:opacity-70 ${shouldBeTransparent ? 'text-white/90' : 'text-charcoal-700'}`}>ESCAPES</a>
            <a href="/collection" className={`text-sm font-light tracking-wide transition-all hover:opacity-70 ${shouldBeTransparent ? 'text-white/90' : 'text-charcoal-700'}`}>COLLECTION</a>
            <a href="/experience" className={`text-sm font-light tracking-wide transition-all hover:opacity-70 ${shouldBeTransparent ? 'text-white/90' : 'text-charcoal-700'}`}>EXPERIENCE</a>
            <a href="/contact" className={`text-sm font-light tracking-wide transition-all hover:opacity-70 ${shouldBeTransparent ? 'text-white/90' : 'text-charcoal-700'}`}>CONTACT</a>
          </nav>
        </div>
      </div>
    </header>
  );
};

// Main Service Page Component
const ServicePage = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();

  const service = serviceData[serviceId];

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-light text-charcoal-900 mb-4">Service not found</h1>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-charcoal-900 text-white text-sm font-light tracking-wide hover:bg-charcoal-800 transition-all"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header isTransparent={true} />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('${service.heroImage}')`
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/50"></div>
        </div>
        
        <div className="relative z-10 text-center text-white max-w-5xl mx-auto px-6">
          <div className="mb-8">
            <h1 className="text-sm font-light tracking-[0.3em] mb-4 opacity-90 uppercase">{service.subtitle}</h1>
            <h2 className="text-6xl lg:text-8xl font-thin leading-none mb-6 tracking-tight">
              {service.name.toUpperCase()}
            </h2>
            <p className="text-xl lg:text-2xl font-light leading-relaxed opacity-80 max-w-4xl mx-auto">
              {service.description}
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-warm-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-sm font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Excellence</h2>
            <h3 className="text-4xl font-thin text-charcoal-900 mb-6 tracking-tight">Our Commitment</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {service.features.map((feature, index) => (
              <div key={index} className="text-center p-6 bg-white rounded-sm shadow-sm border border-warm-gray-100">
                <div className="w-12 h-12 bg-charcoal-100 rounded-full flex items-center justify-center mx-auto mb-4 text-charcoal-600">
                  {feature.icon}
                </div>
                <h4 className="text-lg font-light text-charcoal-900 mb-3">{feature.title}</h4>
                <p className="text-sm font-light text-charcoal-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-sm font-light tracking-[0.3em] text-charcoal-600 mb-4 uppercase">Our Offers</h2>
            <h3 className="text-4xl font-thin text-charcoal-900 mb-6 tracking-tight">Bespoke Solutions</h3>
            <p className="text-lg font-light text-charcoal-600 leading-relaxed max-w-3xl mx-auto">
              Choose the offer that meets your expectations or let us create a completely personalized solution.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {service.packages.map((pkg, index) => (
              <div key={index} className={`p-8 rounded-sm border-2 transition-all duration-300 hover:shadow-lg ${index === 1 ? 'border-charcoal-900 bg-charcoal-900 text-white' : 'border-warm-gray-200 bg-white hover:border-charcoal-300'}`}>
                <div className="text-center mb-8">
                  <h4 className="text-2xl font-light mb-2 tracking-wide">{pkg.name}</h4>
                  <p className={`text-sm font-light mb-4 ${index === 1 ? 'text-white/70' : 'text-charcoal-500'} uppercase tracking-wide`}>
                    {pkg.duration}
                  </p>
                  <p className={`text-base font-light mb-6 leading-relaxed ${index === 1 ? 'text-white/80' : 'text-charcoal-600'}`}>
                    {pkg.description}
                  </p>
                  <div className="text-3xl font-thin mb-6">{pkg.price}</div>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {pkg.includes.map((item, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${index === 1 ? 'bg-white' : 'bg-charcoal-400'}`}></div>
                      <span className={`text-sm font-light leading-relaxed ${index === 1 ? 'text-white/90' : 'text-charcoal-600'}`}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
                
                <button 
                  onClick={() => navigate('/contact')}
                  className={`w-full py-4 text-sm font-light tracking-wide transition-all uppercase ${
                    index === 1 
                      ? 'bg-white text-charcoal-900 hover:bg-warm-gray-100' 
                      : 'bg-charcoal-900 text-white hover:bg-charcoal-800'
                  }`}
                >
                  Request Quote
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-charcoal-900 text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-thin mb-6 tracking-tight">Ready to Experience Excellence?</h2>
          <p className="text-lg font-light opacity-80 mb-8 max-w-2xl mx-auto">
            Our experts are at your disposal to create your unique experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/contact')}
              className="px-8 py-4 bg-white text-charcoal-900 text-sm font-light tracking-wide hover:bg-warm-gray-100 transition-all uppercase"
            >
              Contact Us
            </button>
            <button 
              onClick={() => navigate('/')}
              className="px-8 py-4 border border-white/30 text-white text-sm font-light tracking-wide hover:bg-white/10 transition-all uppercase"
            >
              Back to Home
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-charcoal-900 text-white py-16 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-light tracking-wide mb-4">
            PRIVIA<span className="font-thin">CHARTER</span>
          </h3>
          <p className="text-sm font-light text-white/70">
            The art of escape for privileged souls seeking more than luxury.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ServicePage;